import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface CartItem {
  id: string;
  product_id: string;
  name: string;
  price: number;
  image_url: string;
  quantity: number;
  slug: string;
  weight?: string;
  variant_id?: string;
  variant_name?: string;
  stock?: number; // stok bilgisi, 0 = tükendi
}

interface CartContextType {
  items: CartItem[];
  isOpen: boolean;
  totalItems: number;
  totalPrice: number;
  addItem: (product: Omit<CartItem, 'quantity'>) => void;
  removeItem: (product_id: string, variant_id?: string) => void;
  updateQuantity: (product_id: string, quantity: number, variant_id?: string) => void;
  clearCart: () => void;
  openCart: () => void;
  closeCart: () => void;
}

const CartContext = createContext<CartContextType | null>(null);

const CART_KEY = 'zesha_cart';

function itemKey(product_id: string, variant_id?: string) {
  return variant_id ? `${product_id}__${variant_id}` : product_id;
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem(CART_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  }, [items]);

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const addItem = (product: Omit<CartItem, 'quantity'>) => {
    // Stok 0 ise sepete ekleme
    if (typeof product.stock === 'number' && product.stock <= 0) return;

    setItems(prev => {
      const key = itemKey(product.product_id, product.variant_id);
      const existing = prev.find(i => itemKey(i.product_id, i.variant_id) === key);
      if (existing) {
        // Stok limitini aşma — product.stock her zaman güncel stok bilgisi
        const maxQty = product.stock;
        if (existing.quantity >= maxQty) return prev;
        return prev.map(i =>
          itemKey(i.product_id, i.variant_id) === key
            ? { ...i, quantity: i.quantity + 1, stock: product.stock } // stok bilgisini güncelle
            : i
        );
      }
      // Yeni ürün ekle — stock zorunlu
      return [...prev, { ...product, quantity: 1, stock: product.stock }];
    });
    setIsOpen(true);
  };

  const removeItem = (product_id: string, variant_id?: string) => {
    const key = itemKey(product_id, variant_id);
    setItems(prev => prev.filter(i => itemKey(i.product_id, i.variant_id) !== key));
  };

  const updateQuantity = (product_id: string, quantity: number, variant_id?: string) => {
    if (quantity <= 0) {
      removeItem(product_id, variant_id);
      return;
    }
    const key = itemKey(product_id, variant_id);
    setItems(prev =>
      prev.map(i => {
        if (itemKey(i.product_id, i.variant_id) !== key) return i;
        // Stok limitini aşma — stock number ve > 0 olmalı
        const maxQty = typeof i.stock === 'number' && i.stock > 0 ? i.stock : i.quantity;
        const safeQty = Math.min(quantity, maxQty);
        return { ...i, quantity: safeQty };
      })
    );
  };

  const clearCart = () => setItems([]);
  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);

  return (
    <CartContext.Provider
      value={{ items, isOpen, totalItems, totalPrice, addItem, removeItem, updateQuantity, clearCart, openCart, closeCart }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
